// src/pages/TrackingSystem.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCountryData } from '../components/api';
import TopBar from '../components/TopBar';
import CountryPicker from '../components/CountryPicker';

const TrackingSystem = ({ userClaims, isEmbedded = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Country picker state – no default country selected
  const [selectedCountryOption, setSelectedCountryOption] = useState(null);
  const [selectedCountryIso3, setSelectedCountryIso3] = useState(null);

  // Update filter states to use risk category with 'above80' as default and vaccine with 'BCG' as default
  const [filters, setFilters] = useState({
    Central: { store: '', riskCategory: 'above80', vaccine: 'BCG' },
    Subnational: { store: '', riskCategory: 'above80', vaccine: 'BCG' },
    Regional: { store: '', riskCategory: 'above80', vaccine: 'BCG' },
    Local: { store: '', riskCategory: 'above80', vaccine: 'BCG' }
  });

  // Track current antigen filter for API calls - defaults to BCG
  const [currentAntigenFilter, setCurrentAntigenFilter] = useState('BCG');

  // Fetch country data from API – only when a country is selected or antigen filter changes
  useEffect(() => {
    if (!selectedCountryIso3) {
      setData([]);
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        console.log(`Fetching data for country: ${selectedCountryIso3}, antigen: ${currentAntigenFilter}`);
        const countryData = await fetchCountryData(selectedCountryIso3, currentAntigenFilter);

        const rawData = (() => {
          if (Array.isArray(countryData)) return countryData;
          // Handle common wrapper shapes e.g., { data: [...] } or { results: [...] }
          if (Array.isArray(countryData?.data)) return countryData.data;
          if (Array.isArray(countryData?.results)) return countryData.results;
          // Nothing usable
          return [];
        })();

        // Map API response to legacy structure expected by the component
        const mappedData = rawData.map((rec, idx) => {
          // Compute probability if not supplied ( (min_stock - doses) / min_stock ), clamp 0-1
          const minStock = rec.min_stock;
          const doses = rec.doses;
          const derivedProb = minStock ? (minStock - (doses ?? 0)) / minStock : 0;
          const probability = rec.probability !== undefined ? rec.probability : Math.max(0, Math.min(1, derivedProb));

          return {
            unique_id: rec.lms_id ?? rec.unique_id ?? `unknown-${idx}`,
            store_name: rec.store_name ?? '',
            vaccine_type: rec.antigen_name ?? '',
            country_name: rec.iso3 ?? '',
            report_date: rec.report_date ?? null,
            probability,
            SC_Level: rec.sc_level_no ?? null,
            lms_id: rec.lms_id ?? null,
            parent_store: rec.parent_store ?? '',
            parent_store_id: rec.parent_store_id ?? '',
            Min_Stock: minStock,
            Max_Stock: rec.max_stock,
            Doses: doses,
            // Keep original API properties for parent lookup
            doses: rec.doses,
            min_stock: rec.min_stock,
            max_stock: rec.max_stock,
            antigen_name: rec.antigen_name,
            // Add province and district fields
            province: rec.province ?? '',
            district: rec.district ?? '',
          };
        });

        // Keep only rows from the most recent reporting date to avoid rendering an enormous dataset
        const latestDateStr = mappedData.reduce((max, rec) => {
          if (!rec.report_date) return max;
          return rec.report_date > max ? rec.report_date : max;
        }, '');

        const latestData = latestDateStr
          ? mappedData.filter((rec) => rec.report_date === latestDateStr)
          : mappedData;

        console.log(`Mapped data records: ${mappedData.length}. After latest-date filter: ${latestData.length}`);

        setData(latestData);
      } catch (err) {
        console.error("Error loading country data:", err);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedCountryIso3, currentAntigenFilter]);

  // Group data by level and categorize - moved inside useEffect to depend on data
  const [groupedData, setGroupedData] = useState({});
  
  useEffect(() => {
    if (data.length > 0) {
      const grouped = data.reduce((acc, item) => {
        // Determine level based on sc_level_no from API
        const rawLevel = item.SC_Level ?? item.SC_Level_No;

        let level = 'Local'; // Default fallback
        if (rawLevel !== undefined && rawLevel !== null) {
          if (typeof rawLevel === 'number') {
            // New level mapping: 1=Central, 2=Subnational, 3=Regional, 4=Local
            switch (rawLevel) {
              case 1:
                level = 'Central';
                break;
              case 2:
                level = 'Subnational';
                break;
              case 3:
                level = 'Regional';
                break;
              case 4:
                level = 'Local';
                break;
              default:
                level = 'Local'; // Fallback for any other numbers
            }
          } else if (typeof rawLevel === 'string') {
            // Keep string parsing for backward compatibility
            const lv = rawLevel.toLowerCase();
            if (lv.includes('central') || lv === 'ce' || lv === '1') {
              level = 'Central';
            } else if (lv.includes('sub') || lv === 'su' || lv === '2') {
              level = 'Subnational';
            } else if (lv.includes('regional') || lv === 're' || lv === '3') {
              level = 'Regional';
            }
          }
        }
        if (!acc[level]) {
          acc[level] = [];
        }
        acc[level].push(item);
        return acc;
      }, {});
      setGroupedData(grouped);
    }
  }, [data]);

  // State to track expanded levels
  const [expandedLevels, setExpandedLevels] = useState({});

  // Toggle function for expanding/collapsing levels
  const toggleLevel = (level) => {
    setExpandedLevels((prev) => ({
      ...prev,
      [level]: !prev[level],
    }));
  };

  // Handle store filter changes
  const handleStoreFilterChange = (level, value) => {
    setFilters((prev) => ({
      ...prev,
      [level]: { ...prev[level], store: value },
    }));
  };

  // Handle risk category changes
  const handleRiskCategoryChange = (level, category) => {
    setFilters((prev) => ({
      ...prev,
      [level]: { ...prev[level], riskCategory: category },
    }));
  };

  // Handle antigen filter changes
  const handleVaccineFilterChange = (level, vaccine) => {
    // Update all levels to use the same antigen filter for consistency
    setFilters((prev) => {
      const updatedFilters = {};
      Object.keys(prev).forEach(levelKey => {
        updatedFilters[levelKey] = { ...prev[levelKey], vaccine: vaccine };
      });
      return updatedFilters;
    });
    setCurrentAntigenFilter(vaccine); // Update the state for API calls
  };

  // Get filtered data for a specific level
  const getFilteredData = (level) => {
    if (!groupedData[level]) return [];
    
    let filtered = groupedData[level];
    
    // Apply store name filter
    if (filters[level].store) {
      filtered = filtered.filter((item) =>
        item.store_name.toLowerCase().includes(filters[level].store.toLowerCase())
      );
    }
    
    // Note: Antigen filtering is now handled at the API level
    // The API call includes the antigen parameter, so no client-side filtering needed
    
    // Apply risk category filter
    if (filters[level].riskCategory !== 'all') {
      const threshold = {
        'above80': 0.8,
        'above60': 0.6,
        'above40': 0.4,
        'above20': 0.2,
      }[filters[level].riskCategory];
      
      filtered = filtered.filter((item) => item.probability >= threshold);
    }
    
    return filtered;
  };

  // Helper function to find parent store's vaccine stock
  const getParentStoreStock = (currentItem, allData) => {
    if (!currentItem.parent_store_id || !currentItem.antigen_name) {
      return null;
    }
    // Normalize vaccine type for comparison using antigen_name from API
    const targetVaccine = String(currentItem.antigen_name).trim().toLowerCase();
    const targetParentId = String(currentItem.parent_store_id);
    // Find the parent store record with the same vaccine type (case-insensitive)
    const parentRecord = allData.find(item => {
      const parentVaccine = String(item.antigen_name).trim().toLowerCase();
      return item.lms_id === targetParentId && parentVaccine === targetVaccine;
    });
    return parentRecord ? {
      doses: parentRecord.doses,
    } : null;
  };

  // Handle creating ticket for store
  const handleCreateTicketForStore = (storeItem) => {
    const prefillTicket = {
      title: `Stock-out risk for ${storeItem.store_name} (${storeItem.vaccine_type || 'Antigen'})`,
      country_code: selectedCountryOption?.label || selectedCountryIso3 || '',
      country_iso3: selectedCountryIso3 || '',
      level_id: Number(storeItem.SC_Level) || '',
      store_name: storeItem.store_name || '',
      thrive_id: storeItem.lms_id || storeItem.unique_id || '',
      lms_id: storeItem.lms_id || '',
      parent_store: storeItem.parent_store || '',
      antigen_name: storeItem.vaccine_type ? [storeItem.vaccine_type] : [],
      priority: storeItem.probability >= 0.8 ? 'High' : 'Medium',
      description: `Auto-filled from Tracking System.\n\nStore: ${storeItem.store_name || 'N/A'}\nParent Store: ${storeItem.parent_store || 'N/A'}\nAntigen: ${storeItem.vaccine_type || 'N/A'}\nRisk: ${Math.round((storeItem.probability || 0) * 100)}%\nCurrent stock: ${storeItem.Doses ?? 'N/A'}\nMin stock: ${storeItem.Min_Stock ?? 'N/A'}\nMax stock: ${storeItem.Max_Stock ?? 'N/A'}`
    };

    navigate('/ticketing-system', {
      state: {
        openCreateModal: true,
        prefillTicket
      }
    });
  };

  // Define the order of levels
  const orderedLevels = ['Central', 'Subnational', 'Regional', 'Local'];

  const content = (
    <div className="p-4">
      <div className="bg-white shadow-md rounded-lg p-4">
          {/* Country selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
            <CountryPicker
              value={selectedCountryOption}
              onChange={(opt) => {
                setSelectedCountryOption(opt);
                // Use the ISO3 from the CountryPicker component
                const iso3 = opt?.iso3 || (typeof opt?.value === 'string' ? opt.value.toUpperCase() : null);
                setSelectedCountryIso3(iso3);
                console.log('Selected country:', opt?.label, 'ISO3:', iso3);
              }}
              placeholder="Select or search for country..."
              excludeIso3={["ERT"]}
            />
          </div>
          {/* Loading and error states */}
          {!selectedCountryIso3 && (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="text-gray-500 text-lg mb-2">Please select a country to view tracking data</div>
                <div className="text-gray-400 text-sm">Choose a country from the dropdown above to see stock-out risk information</div>
              </div>
            </div>
          )}
          
          {loading && selectedCountryIso3 && (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          )}
          
          {!loading && !error && selectedCountryIso3 && (
            <>
              {orderedLevels.map((level) => (
                groupedData[level] && (
                  <div key={level} className="mb-8">
                    <h2 className="text-xl font-semibold mb-2">Level: {level}</h2>
                    <p className="mb-4">This table shows the stock-out situations for level {level}.</p>
                    
                    {/* Updated filter section with buttons */}
                    <div className="bg-gray-100 p-4 mb-4 rounded-lg">
                      <h3 className="text-lg font-medium mb-2">Filters</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Store Name
                          </label>
                          <input
                            type="text"
                            className="w-full p-2 border rounded-md"
                            placeholder="Filter by store name..."
                            value={filters[level].store}
                            onChange={(e) => handleStoreFilterChange(level, e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vaccine Type
                          </label>
                          <select
                            className="w-full p-2 border rounded-md"
                            value={filters[level].vaccine}
                            onChange={(e) => handleVaccineFilterChange(level, e.target.value)}
                          >
                            <option value="BCG">BCG</option>
                            <option value="OPV">OPV</option>
                            <option value="IPV">IPV</option>
                            <option value="DPT">DPT</option>
                            <option value="HepB">HepB</option>
                            <option value="Hib">Hib</option>
                            <option value="PCV">PCV</option>
                            <option value="Rotavirus">Rotavirus</option>
                            <option value="Measles">Measles</option>
                            <option value="Yellow Fever">Yellow Fever</option>
                            <option value="TT">TT</option>
                            <option value="all">All Vaccines</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Risk Category
                          </label>
                          <div className="flex gap-2">
                            {['above80', 'above60', 'above40', 'above20', 'all'].map((category) => (
                              <button
                                key={category}
                                onClick={() => handleRiskCategoryChange(level, category)}
                                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                  filters[level].riskCategory === category
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {category === 'above80' ? '>80%' : 
                                 category === 'above60' ? '>60%' : 
                                 category === 'above40' ? '>40%' : 
                                 category === 'above20' ? '>20%' : 'All'}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expandable level section */}
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleLevel(level)}
                        className={`w-full px-6 py-4 text-left transition-colors flex justify-between items-center ${
                          level === 'Central' ? 'bg-blue-600 hover:bg-blue-700' :
                          level === 'Subnational' ? 'bg-green-600 hover:bg-green-700' :
                          level === 'Regional' ? 'bg-orange-600 hover:bg-orange-700' :
                          'bg-purple-600 hover:bg-purple-700'
                        }`}
                      >
                        <div>
                          <h3 className={`text-lg font-semibold ${
                            level === 'Central' ? 'text-white' :
                            level === 'Subnational' ? 'text-white' :
                            level === 'Regional' ? 'text-white' :
                            'text-white'
                          }`}>
                            {level} Level ({getFilteredData(level).length} stores)
                          </h3>
                          <p className={`text-sm ${
                            level === 'Central' ? 'text-blue-100' :
                            level === 'Subnational' ? 'text-green-100' :
                            level === 'Regional' ? 'text-orange-100' :
                            'text-purple-100'
                          }`}>
                            Click to {expandedLevels[level] ? 'collapse' : 'expand'} and view details
                          </p>
                        </div>
                        <svg
                          className={`w-5 h-5 transition-transform ${
                            level === 'Central' ? 'text-blue-100' :
                            level === 'Subnational' ? 'text-green-100' :
                            level === 'Regional' ? 'text-orange-100' :
                            'text-purple-100'
                          } ${expandedLevels[level] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expandedLevels[level] && (
                        <div className="border-t border-gray-200">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className={`${
                                level === 'Central' ? 'bg-blue-600' :
                                level === 'Subnational' ? 'bg-green-600' :
                                level === 'Regional' ? 'bg-orange-600' :
                                'bg-purple-600'
                              }`}>
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Store Name
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Parent Store
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Vaccine Type
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Stock Out Risk
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Current Status (Min/Max)
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {getFilteredData(level).map((item, index) => {
                                  // Generate progress bar content
                                  let progressBarContent = 'Loading...';
                                  // Use data directly from the main API response
                                  if (item.Doses !== undefined && item.Min_Stock !== undefined && item.Max_Stock !== undefined) {
                                    const actual = item.Doses;
                                    const min = item.Min_Stock;
                                    const max = item.Max_Stock;

                                    // Handle case when stock is 0
                                    if (actual === 0) {
                                      progressBarContent = (
                                        <div className="flex flex-col">
                                          <span className="text-sm font-bold text-center">Available Stock: {actual}</span>
                                          <div className="relative w-full h-4 bg-gray-200 rounded overflow-hidden">
                                            <div
                                              className="absolute top-0 left-0 h-4 bg-red-500 rounded"
                                              style={{ width: '100%' }}
                                            ></div>
                                            <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-bold">
                                              No Stock
                                            </span>
                                          </div>
                                          <div className="flex justify-between text-xs mt-1">
                                            <span>Min: {min}</span>
                                            <span>Max: {max}</span>
                                          </div>
                                        </div>
                                      );
                                    } else if (actual < min) {
                                      const progress = ((actual - min) / (max - min)) * 100;
                                      const barWidth = Math.min(Math.abs(progress), 100);
                                      progressBarContent = (
                                        <div className="flex flex-col">
                                          <span className="text-sm font-bold text-center">Available Stock: {actual}</span>
                                          <div className="relative w-full h-4 bg-gray-200 rounded overflow-hidden">
                                            <div
                                              className="absolute top-0 left-0 h-4 bg-red-500 rounded"
                                              style={{ width: `${barWidth}%` }}
                                            ></div>
                                            <span className="absolute inset-0 flex items-center justify-center text-xs text-black">
                                              {progress.toFixed(0)}%
                                            </span>
                                          </div>
                                          <div className="flex justify-between text-xs mt-1">
                                            <span>Min: {min}</span>
                                            <span>Max: {max}</span>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      // Calculate progress percentage (can be >100)
                                      const progress = max !== min ? ((actual - min) / (max - min)) * 100 : 0;
                                      const barWidth = Math.min(progress, 100);
                                      progressBarContent = (
                                        <div className="flex flex-col">
                                          <span className="text-sm font-bold text-center">Available Stock: {actual}</span>
                                          <div className="relative w-full h-4 bg-gray-200 rounded overflow-hidden">
                                            <div
                                              className="absolute top-0 left-0 h-4 bg-green-500 rounded"
                                              style={{ width: `${barWidth}%` }}
                                            ></div>
                                            <span className="absolute inset-0 flex items-center justify-center text-xs text-black">
                                              {progress.toFixed(0)}%
                                            </span>
                                          </div>
                                          <div className="flex justify-between text-xs mt-1">
                                            <span>Min: {min}</span>
                                            <span>Max: {max}</span>
                                          </div>
                                        </div>
                                      );
                                    }
                                  } else {
                                    progressBarContent = 'No Data';
                                  }
                                  
                                  return (
                                    <tr key={index} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">
                                          {item.store_name_original || item.store_name}
                                        </div>
                                        {/* Latest report date */}
                                        {item.report_date && (
                                          <div className="text-xs text-gray-500">
                                            Latest Report: {new Date(item.report_date).toLocaleDateString()}
                                          </div>
                                        )}
                                        <span className="text-xs text-gray-500">
                                          {item.province ? `Province: ${item.province}` : ''}, {item.district ? `District: ${item.district}` : ''}, {item.lga ? `LGA: ${item.lga}` : ''}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                          <span className="text-sm">{item.parent_store || 'N/A'}</span>
                                          <span className="text-xs">{item.parent_store_id ? `ID: ${item.parent_store_id}` : 'No Parent Store'}</span>
                                          {(() => {
                                            const parentStock = getParentStoreStock(item, data);
                                            if (parentStock) {
                                              return (
                                                <div className="mt-1 p-2 bg-gray-50 rounded border">
                                                  <div className="text-xs font-semibold text-gray-700">
                                                    {item.vaccine_type} Stock in Parent:
                                                  </div>
                                                  <div className="text-xs text-gray-600">
                                                    Available: {parentStock.doses || 0}
                                                  </div>
                                                </div>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.vaccine_type}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                          item.probability >= 0.8
                                            ? 'bg-red-100 text-red-800'
                                            : item.probability >= 0.6
                                            ? 'bg-orange-100 text-orange-800'
                                            : item.probability >= 0.4
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : item.probability >= 0.2
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-green-100 text-green-800'
                                        }`}>
                                          {Math.round(item.probability * 100)}%
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {progressBarContent}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                          onClick={() => handleCreateTicketForStore(item)}
                                          className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md transition-colors"
                                        >
                                          Create Ticket
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              ))}
            </>
          )}
      </div>
    </div>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="tracking" userClaims={userClaims} />
      {content}
    </div>
  );
};

export default TrackingSystem;